import React, { useEffect, useMemo, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useDisableScrollOnFocus } from "../../../Hooks/useDisableScrollOnFocus";
import InfoIcon from "./InfoIcon";
import { ADD_ITEMS } from "../stoctTableInfo";
import { buildResearchFieldPrefill } from "../utils/researchQuickAdd";
import {
	productForms,
	packageTypes,
	otcProductForms,
	otcPackageTypes,
} from "../../PharmacyMedicineAdd/UpdateMedicine";
import CreatableOptionSelect from "../../PharmacyMedicineAdd/components/CreatableOptionSelect";

const FieldError = ({ msg }) =>
	msg ? <small className="text-danger d-block mt-1">{msg}</small> : null;

const validateFields = (form, t) => {
	const errs = {};
	if (!form.package?.trim()) errs.package = t("Packaging Type is required");
	if (!form.productForm?.trim()) errs.productForm = t("Product form is required");
	if (!form.packageQuantity || Number(form.packageQuantity) <= 0) {
		errs.packageQuantity = t("Units per pack must be greater than 0");
	}
	if (!form.regularPrice || Number(form.regularPrice) <= 0) {
		errs.regularPrice = t("Pack MRP must be greater than 0");
	}
	return errs;
};

const ResearchItemDetailsModal = ({
	isOpen,
	onClose,
	medicine,
	isOTC,
	onConfirm,
	submitting = false,
}) => {
	const { t } = useTranslation();
	const disableScrollProps = useDisableScrollOnFocus();
	const [form, setForm] = useState(() => buildResearchFieldPrefill(medicine));
	const [errors, setErrors] = useState({});

	const itemLabel = useMemo(() => {
		if (!medicine) return "";
		if (isOTC) return medicine.name || "";
		const brand = medicine.name || "";
		const composition =
			medicine.salt_composition ||
			medicine.salt_composition_new ||
			"";
		return brand && composition && brand !== composition
			? `${brand} · ${composition}`
			: brand || composition;
	}, [medicine, isOTC]);

	useEffect(() => {
		if (isOpen && medicine) {
			setForm(buildResearchFieldPrefill(medicine));
			setErrors({});
		}
	}, [isOpen, medicine]);

	const set = (field, value) => {
		setForm((prev) => ({ ...prev, [field]: value }));
		if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
	};

	const handleSubmit = () => {
		const errs = validateFields(form, t);
		if (Object.keys(errs).length) {
			setErrors(errs);
			return;
		}
		onConfirm({
			package: form.package.trim(),
			productForm: form.productForm.trim(),
			packageQuantity: form.packageQuantity,
			regularPrice: form.regularPrice,
			minQuantityAlert: form.minQuantityAlert,
		});
	};

	return (
		<Modal isOpen={isOpen} toggle={onClose} size="lg" backdrop="static" className="research-details-modal">
			<ModalHeader toggle={onClose}>
				<span className="quick-add-modal-title">
					{t("Complete Item Details")}
					<small className="quick-add-modal-subtitle">
						{t("Some required details are missing for this item. Review and confirm before adding.")}
					</small>
				</span>
			</ModalHeader>

			<ModalBody>
				{itemLabel && (
					<div className="research-details-item-banner mb-3">
						<small className="text-muted d-block mb-1">{t("Adding to inventory")}</small>
						<strong className="research-details-item-name">{itemLabel}</strong>
					</div>
				)}

				<div className="row g-3">
					<div className="col-md-6">
						<Label className="form-label">
							{t("Packaging Type")} <span className="text-danger">*</span>
							&nbsp;
							<InfoIcon text={ADD_ITEMS.packagingType} />
						</Label>
						<CreatableOptionSelect
							options={isOTC ? otcPackageTypes : packageTypes}
							value={form.package}
							onChange={(value) => set("package", value)}
							placeholder={t("Search or type packaging")}
							isDisabled={submitting}
							inModal
						/>
						<FieldError msg={errors.package} />
					</div>

					<div className="col-md-6">
						<Label className="form-label">
							{t("Product Form")} <span className="text-danger">*</span>
							&nbsp;
							<InfoIcon text={ADD_ITEMS.productForm} />
						</Label>
						<CreatableOptionSelect
							options={isOTC ? otcProductForms : productForms}
							value={form.productForm}
							onChange={(value) => set("productForm", value)}
							placeholder={t("Search or type form")}
							isDisabled={submitting}
							inModal
						/>
						<FieldError msg={errors.productForm} />
					</div>

					<div className="col-md-6">
						<Label className="form-label">
							{t("Units per Pack")} <span className="text-danger">*</span>
							&nbsp;
							<InfoIcon text={ADD_ITEMS.unitQuantity} />
						</Label>
						<Input
							type="number"
							min={1}
							value={form.packageQuantity}
							onChange={(e) => set("packageQuantity", e.target.value)}
							placeholder={t("e.g. 10, 30, 100")}
							invalid={!!errors.packageQuantity}
							disabled={submitting}
							{...disableScrollProps}
						/>
						<FieldError msg={errors.packageQuantity} />
					</div>

					<div className="col-md-6">
						<Label className="form-label">
							{t("Pack MRP")} <span className="text-danger">*</span>
						</Label>
						<Input
							type="number"
							min={0}
							step="0.01"
							value={form.regularPrice}
							onChange={(e) => set("regularPrice", e.target.value)}
							placeholder={t("Maximum Retail Price")}
							invalid={!!errors.regularPrice}
							disabled={submitting}
							{...disableScrollProps}
						/>
						<FieldError msg={errors.regularPrice} />
					</div>

					<div className="col-md-6">
						<Label className="form-label">
							{t("Minimum Stock Level (Alert)")}
							&nbsp;
							<InfoIcon text={ADD_ITEMS.minimumAlertQuantity} />
						</Label>
						<Input
							type="number"
							min={0}
							value={form.minQuantityAlert}
							onChange={(e) => set("minQuantityAlert", e.target.value)}
							placeholder={t("Set low stock alert quantity")}
							disabled={submitting}
							{...disableScrollProps}
						/>
					</div>
				</div>
			</ModalBody>

			<ModalFooter className="research-details-modal-footer">
				<Button color="primary" onClick={handleSubmit} disabled={submitting}>
					{submitting ? t("Adding...") : t("Add Item & Attach to Purchase")}
				</Button>
				<Button color="secondary" onClick={onClose} disabled={submitting}>
					{t("Cancel")}
				</Button>
			</ModalFooter>
		</Modal>
	);
};

export default ResearchItemDetailsModal;
